import {
  captureClientException,
  getClientPostHogHeaders,
} from "@/lib/analytics/client";
import { ApiError } from "@/lib/error-handling";

export interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

/**
 * API Client with standardized error handling
 */
export async function apiClient<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = new URL(endpoint, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }
  // PostHog session/distinct-id headers let server-side error capture
  // (instrumentation.ts onRequestError, middleware $exception) attach
  // `$session_id` to events, so server errors share the replay timeline
  // with the originating browser session. Absent when the SDK has not
  // loaded yet (POSTHOG_PROJECT_TOKEN unset, or before `<PostHogProvider />`).
  const posthogHeaders = getClientPostHogHeaders();
  const headers = {
    "Content-Type": "application/json",
    ...(posthogHeaders ?? {}),
    ...options.headers,
  };
  try {
    const response = await fetch(url.toString(), {
      ...fetchOptions,
      headers,
    });
    let data;
    let text = undefined;
    try {
      data = await response.json();
    } catch {
      try {
        text = await response.text();
      } catch {
        text = undefined;
      }
      data = undefined;
    }
    if (!response.ok) {
      // Redirect deactivated users to the forbidden page instead of showing a toast.
      // Skip if already on /auth/forbidden to avoid infinite redirect loop.
      if (
        response.status === 403 &&
        data?.error === "Access restricted. Account is deactivated." &&
        !window.location.pathname.startsWith("/auth/forbidden")
      ) {
        window.location.href = "/auth/forbidden";
        // Return a never-resolving promise to halt the call chain while redirect is in progress
        return new Promise<T>(() => {});
      }

      // Redirect to login on session expiry.
      // Skip if already on /login to avoid infinite redirect loop.
      if (
        response.status === 401 &&
        !window.location.pathname.startsWith("/login")
      ) {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.href = `/login?returnTo=${returnTo}`;
        return new Promise<T>(() => {});
      }

      const apiError = new ApiError(
        data?.error || text || response.statusText || "Request failed",
        {
          status: response.status,
          details: data?.details || text,
          code: data?.code,
        }
      );
      // Re-emit only server-side failures (5xx). 4xx (validation, auth,
      // expected not-found, prefetch noise) must not trigger session replay.
      if (response.status >= 500) {
        captureClientException(apiError);
      }
      throw apiError;
    }
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    // Network / parse failures: SDK never saw a status, treat as 5xx-equivalent
    // for replay purposes — the browser still has the buffer and the user
    // session is the one we want to record.
    if (error instanceof Error) {
      const networkError = new ApiError(
        error.message || "Network or parsing error",
        {
          status: 0,
        }
      );
      captureClientException(networkError);
      throw networkError;
    }
    const unknownError = new ApiError("Unknown error occurred");
    captureClientException(unknownError);
    throw unknownError;
  }
}
